import { useState, type ReactNode } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { usePwlLabQuestComplete } from '@hooks/register/use-pwl-lab-quest-complete';
import { useLocalize, type TLocalize } from '@hooks/use-localize';
import { useSession } from '@hooks/security/use-session';
import { useStepForm } from '@hooks/use-step-form';
import { pwlLabQuestCompleteSchema } from '@views/register/pwl/pwl-lab-quest-complete.schema';
import type {
    IPwlLabQuestCompleteFormValues,
    IPwlLabQuestCompleteModel,
    IPwlLabQuestCompleteSubmitResult,
} from '@/types/history/pwl-lab-quest-complete';
import PageLoader from '@components/page-loader';
import FieldError from '@components/field-error';
import RequiredFieldsAlert from '@components/required-fields-alert';
import MentorOfferSection from '@views/register/pwl/lab-orders-sections/mentor-offer-section';

const QUEST_LOCATION_FINDER_URL = 'https://secure.questdiagnostics.com/hcp/psc/jsp/SearchLocation.do?newSearch=FindLocation';

const PwlLabQuestComplete = () => {
    const { registrationId, editKey } = useSession();
    const { model, isLoading, error, submit, isSubmitting, submitError } = usePwlLabQuestComplete(registrationId, editKey);

    return (
        <div className="container-fluid">
            <PageLoader isLoading={isLoading} error={error}>
                {!error && (
                    <PwlLabQuestCompleteContent
                        model={model}
                        isSubmitting={isSubmitting}
                        submitError={submitError}
                        onSubmit={submit}
                    />
                )}
            </PageLoader>
        </div>
    );
};

interface IPwlLabQuestCompleteContentProps {
    model: IPwlLabQuestCompleteModel;
    isSubmitting: boolean;
    submitError: string | null;
    onSubmit: (values: IPwlLabQuestCompleteFormValues) => Promise<IPwlLabQuestCompleteSubmitResult>;
}

const PwlLabQuestCompleteContent = ({ model, isSubmitting, submitError, onSubmit }: IPwlLabQuestCompleteContentProps) => {
    const t = useLocalize(model.languageData);
    const [isComplete, setIsComplete] = useState(false);

    const {
        register,
        onSubmit: handleFormSubmit,
        formState: { errors },
    } = useStepForm<IPwlLabQuestCompleteFormValues, IPwlLabQuestCompleteSubmitResult>({
        resolver: zodResolver(pwlLabQuestCompleteSchema),
        defaultValues: {
            questCompletedDate: model.questCompletedDate.slice(0, 10),
            questCompletedWhere: model.questCompletedWhere,
        },
        submit: onSubmit,
        linkMap: [],
        onSuccess: () => setIsComplete(true),
    });

    if (isComplete) {
        return (
            <QuestCompleteLayout model={model} t={t}>
                <div className="row row-margined">
                    <div className="col-xs-12">
                        <h1>Thank you!</h1>
                        <p>{t('DashLabel_WeWillInvestigateToSeeIfWeCanFindYourLabs', 'We will investigate to see if we can find your labs')}.</p>
                    </div>
                </div>
            </QuestCompleteLayout>
        );
    }

    const hasErrors = Object.keys(errors).length > 0;

    return (
        <QuestCompleteLayout model={model} t={t}>
            <div className="row row-margined">
                <div className="col-xs-12">
                    <h1>{t('DashLabel_QuestLabsAlreadyCompleted', 'Quest Labs Already Completed')}?</h1>
                </div>
            </div>

            <RequiredFieldsAlert show={hasErrors} t={t} />

            <div className="row row-margined">
                <div className="col-xs-12">
                    <p>
                        {t(
                            'DashLabel_InOrderToFollowUpWeNeedYouToProvideTheFollowingInformation',
                            'In order to follow up, we need you to provide the following information about your Quest Labs visit. Then, we will follow up with Quest Labs to expedite the return of your results report',
                        )}
                        .
                    </p>
                </div>
            </div>

            <div className="row row-margined">
                <div className="col-xs-12">
                    <form onSubmit={handleFormSubmit}>
                        <div className="form-group">
                            <label className="control-label" htmlFor="QuestCompletedDate">
                                <FieldError message={errors.questCompletedDate?.message} required />{' '}
                                {t('DashLabel_WhenDidYouGoToQuestLabs', 'When did you go to Quest Labs')}:
                            </label>
                            <input
                                id="QuestCompletedDate"
                                type="date"
                                className="form-control"
                                style={{ maxWidth: 200 }}
                                {...register('questCompletedDate')}
                            />
                        </div>

                        <div className="form-group">
                            <label className="control-label" htmlFor="QuestCompletedWhere">
                                <FieldError message={errors.questCompletedWhere?.message} />{' '}
                                {t('DashLabel_WhichQuestLabsFacilityDidYouGoTo', 'Which Quest Labs facility did you go to')}?
                            </label>
                            <textarea
                                id="QuestCompletedWhere"
                                className="form-control"
                                {...register('questCompletedWhere')}
                            />

                            <div
                                className="alert alert-info"
                                style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 8, marginBottom: 0, padding: '10px 12px', fontSize: 14 }}
                            >
                                <span className="glyphicon glyphicon-map-marker" aria-hidden="true" style={{ marginTop: 3 }}></span>
                                <span>
                                    {t('DashLabel_CantRememberWhichQuestLocation', "Can't remember which location? Look it up with the")}{' '}
                                    <a className="alert-link" href={QUEST_LOCATION_FINDER_URL} target="_blank" rel="noopener noreferrer">
                                        {t('DashLabel_QuestLabsLocationFinder', 'Quest Labs Location Finder')}
                                    </a>
                                </span>
                            </div>
                        </div>

                        {submitError && (
                            <div className="alert alert-danger" style={{ marginTop: 20 }}>
                                {submitError}
                            </div>
                        )}

                        <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                            Continue
                        </button>
                    </form>
                </div>
            </div>
        </QuestCompleteLayout>
    );
};

interface IQuestCompleteLayoutProps {
    model: IPwlLabQuestCompleteModel;
    t: TLocalize;
    children: ReactNode;
}

const QuestCompleteLayout = ({ model, t, children }: IQuestCompleteLayoutProps) => (
    <div className="row row-margined">
        <div className="col-xs-12 col-sm-7 col-sm-offset-0">{children}</div>

        <div className="col-xs-12 col-sm-5">
            {model.showMentorOffer && <MentorOfferSection t={t} />}
        </div>
    </div>
);

export default PwlLabQuestComplete;
